import React, { FC, useEffect, useState } from 'react';
import { Box, Button, Flex, SimpleGrid, Text, TextInput } from '@mantine/core';
import { useForm, FormErrors } from '@mantine/form';
import { useAppSelector } from '@hooks/redux/redux';
import { SelectWithPagination } from '@/containers/pages/filials/components/select';
import { useSaveFilial } from '@/containers/pages/filials/hooks/useSaveFilial';
import { IFilialCommonForm } from '@/entities/admin-app/filials';
import { useStyles } from '../../../styles';
import { isSaaS } from '@/lib/utils/access';
import { useTranslation } from 'react-i18next';
import { getValidateErrors } from '@/lib/utils/validation';
import { settingsPageValidateScheme } from '@/containers/pages/filials/schemes/validate';

const SettingsPage: FC = () => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const { filial } = useAppSelector((state) => state.filials);
  const { Settings } = useAppSelector((state) => state.userInfo);
  const [isSaving, setIsSaving] = useState(false);
  const saveFilial = useSaveFilial();

  const form = useForm<Partial<IFilialCommonForm>>({
    initialValues: {
      Title: '',
      Company: '',
      Parent: {
        Id: null,
        Name: null
      },
      IsParentSettings: false
    },
    validateInputOnChange: true,
    validate: (values: Partial<IFilialCommonForm>): FormErrors =>
      getValidateErrors(settingsPageValidateScheme(t), values)
  });

  const errors = form.errors;
  const isDirtyForm = form.isDirty();
  const isDisabled = Object.keys(errors)?.length !== 0 || !isDirtyForm;

  const isMain = filial?.Parent === null && filial?.IsRoot;

  useEffect(() => {
    if (!filial) {
      return;
    }
    const fromData = {
      IsParentSettings: false,
      ...filial,
      Settings: filial.Settings || undefined,
      Parent:
        isMain || isSaaS(Settings)
          ? null
          : {
              Id: filial?.Parent?.Id || null,
              Name: filial?.Parent?.Name || null
            }
    };
    form.setValues(fromData);
    form.resetDirty(fromData);
  }, [filial]);

  const handleSubmit = async (values: Partial<IFilialCommonForm>) => {
    if (
      !values.Title ||
      !values.Company ||
      values.Parent === undefined ||
      values.IsParentSettings === undefined
    ) {
      return;
    }
    await setIsSaving(true);
    await saveFilial(filial, values as IFilialCommonForm);
    await setIsSaving(false);
  };

  const transformedOptions = form.getTransformedValues();

  return (
    <Box className={classes.container}>
      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Flex className={classes.section} align="flex-start" direction="column">
          <Text className={classes.title}>{t('filials.settings.general')}</Text>

          <SimpleGrid cols={{ base: 1, xs: 2 }} className={classes.listRow}>
            <TextInput
              className={classes.textInput}
              placeholder={t('filials.settings.name')}
              label={t('filials.settings.filialName')}
              withAsterisk
              data-value="Title"
              {...form.getInputProps('Title')}
            />

            {!isSaaS(Settings) && (
              <>
                <TextInput
                  className={classes.textInput}
                  placeholder={t('filials.settings.filialCode')}
                  label={t('filials.settings.filialCode')}
                  data-value="Company"
                  {...form.getInputProps('Company')}
                />
                {!isMain && (
                  <SelectWithPagination
                    form={form}
                    fieldName="Parent.Id"
                    value={isMain ? null : transformedOptions?.Parent?.Id}
                    label={t('filials.settings.filialParent')}
                    placeholder={t('choose')}
                    list={[]}
                    SkipCustomers={filial?.Id ? [filial?.Id] : []}
                    filialName={form.getInputProps('Parent.Name').value}
                    customerField="Parent.Name"
                  />
                )}
              </>
            )}
          </SimpleGrid>
          <Button
            type="submit"
            size="sm"
            radius={8}
            disabled={isDisabled}
            mt="md"
            style={{ padding: '0 20px' }}
            loading={isSaving}
          >
            {t('save')}
          </Button>
        </Flex>
      </form>
    </Box>
  );
};

export default SettingsPage;
